import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_URL from '../api';
import logoMark from '../assets/images/logo.jpg';

// ── helpers ───────────────────────────────────────────────────────────────────
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)  return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function fullTime(dateStr) {
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

// ── ConvoItem (left panel row) ────────────────────────────────────────────────
function ConvoItem({ c, active, myId, onClick }) {
  const name   = c.other?.name || 'Unknown';
  const sub    = c.other?.company_name ? `${c.other.company_name}` : c.otherType;
  const isMe   = c.lastMsg.sender_id === myId || c.lastMsg.sender_id?.toString() === myId;
  const preview = c.lastMsg.attachment_name
    ? `📎 ${c.lastMsg.attachment_name}`
    : c.lastMsg.body?.slice(0, 60) || '';

  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', width: '100%',
      background: active ? 'var(--primary-light)' : 'transparent',
      border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left',
      borderLeft: active ? '3px solid var(--primary)' : '3px solid transparent',
      transition: 'background 0.15s',
    }}>
      {/* Avatar */}
      <div style={{
        width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
        background: active ? 'var(--primary)' : '#E0E7FF', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 14,
        color: active ? '#fff' : 'var(--primary)',
      }}>
        {initials(name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
          <span style={{ fontWeight: c.unread ? 700 : 600, fontSize: 14, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
            {name}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, marginLeft: 6 }}>
            {timeAgo(c.lastMsg.sent_at)}
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {sub && <span style={{ color: '#818CF8', fontWeight: 500, marginRight: 4 }}>{sub} ·</span>}
          {isMe ? <span style={{ color: 'var(--text-muted)' }}>You: </span> : null}
          {preview}
        </div>
      </div>
      {c.unread > 0 && (
        <span style={{ flexShrink: 0, background: 'var(--primary)', color: '#fff', borderRadius: 20, fontSize: 10, fontWeight: 700, padding: '2px 7px', marginLeft: 4 }}>
          {c.unread}
        </span>
      )}
    </button>
  );
}

// ── Bubble ─────────────────────────────────────────────────────────────────────
function Bubble({ msg, isMine }) {
  const hasBody = !!msg.body;
  const hasFile = !!msg.attachment_url;

  return (
    <div style={{ display: 'flex', flexDirection: isMine ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 8, marginBottom: 10 }}>
      {/* Avatar */}
      <div style={{
        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
        background: isMine ? '#818CF8' : '#E0E7FF',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 11, color: isMine ? '#fff' : 'var(--primary)',
      }}>
        {initials(isMine ? 'Me' : '?')}
      </div>

      <div style={{ maxWidth: '68%', display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start' }}>
        <div style={{
          background: isMine ? 'var(--primary)' : '#fff',
          color:      isMine ? '#fff' : 'var(--text-primary)',
          border:     isMine ? 'none' : '1px solid var(--border)',
          borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          padding: '10px 14px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          wordBreak: 'break-word',
        }}>
          {hasBody && <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55 }}>{msg.body}</p>}
          {hasFile && (
            <a href={`${API_URL}${msg.attachment_url}`} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: hasBody ? 8 : 0,
                color: isMine ? '#E0E7FF' : 'var(--primary)', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
              {msg.attachment_name || 'Attachment'}
            </a>
          )}
        </div>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3, paddingLeft: 2, paddingRight: 2 }}>
          {fullTime(msg.sent_at)}
        </span>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function Messaging({ user }) {
  const navigate    = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('token');
  const myId  = user?._id || user?.id || '';

  // Left panel
  const [convos,   setConvos]   = useState([]);
  const [convLoading, setConvLoading] = useState(true);
  const [search,   setSearch]   = useState('');
  const [activeKey, setActiveKey] = useState(null);

  // Right panel
  const [thread,  setThread]   = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [activeOther, setActiveOther] = useState(null); // { id, type, name, company }

  // Mobile panel toggle — on phones show either list or chat
  const [mobileChatOpen, setMobileChatOpen] = useState(false);

  // Compose
  const [body,    setBody]   = useState('');
  const [file,    setFile]   = useState(null);
  const [sending, setSending] = useState(false);

  const bottomRef = useRef(null);
  const fileRef   = useRef(null);

  // ── fetch conversation list ─────────────────────────────────────────────────
  const fetchConvos = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConvos(res.data);
    } catch { /* silent */ }
    finally { setConvLoading(false); }
  }, [token]);

  useEffect(() => { fetchConvos(); }, [fetchConvos]);

  // ── open convo passed via ?with=ID&type=Recruiter|Student&name=... ──────────
  useEffect(() => {
    const withId   = searchParams.get('with');
    const withType = searchParams.get('type');
    const withName = searchParams.get('name');
    const withCo   = searchParams.get('company');
    if (withId && withType) {
      openConvo({ otherId: withId, otherType: withType, other: { name: withName || withId, company_name: withCo } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── open a conversation ─────────────────────────────────────────────────────
  const openConvo = useCallback(async (c) => {
    const key = [myId, c.otherId].sort().join('_');
    setActiveKey(key);
    setActiveOther(c);
    setThread([]);
    setThreadLoading(true);
    setMobileChatOpen(true);
    try {
      const res = await axios.get(`${API_URL}/api/messages/conversation/${c.otherId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThread(res.data);
      // refresh convo list so unread clears
      fetchConvos();
    } catch { /* silent */ }
    finally { setThreadLoading(false); }
  }, [myId, token, fetchConvos]);

  // scroll to bottom whenever thread changes
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread]);

  // ── send ────────────────────────────────────────────────────────────────────
  const handleSend = async (e) => {
    e.preventDefault();
    if (!body.trim() && !file) return;
    if (!activeOther) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('receiver_id',   activeOther.otherId);
      fd.append('receiver_type', activeOther.otherType);
      if (body.trim()) fd.append('body', body.trim());
      if (file) fd.append('attachment', file);
      const res = await axios.post(`${API_URL}/api/messages/send`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setThread(t => [...t, res.data]);
      setBody('');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      fetchConvos();
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  // ── delete conversation ─────────────────────────────────────────────────────
  const handleDelete = async (otherId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await axios.delete(`${API_URL}/api/messages/conversation/${otherId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (activeOther?.otherId === otherId) { setActiveKey(null); setActiveOther(null); setThread([]); }
      fetchConvos();
    } catch { /* silent */ }
  };

  // ── filtered convos ─────────────────────────────────────────────────────────
  const filtered = convos.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (c.other?.name || '').toLowerCase().includes(q) ||
           (c.other?.company_name || '').toLowerCase().includes(q);
  });

  const totalUnread = convos.reduce((s, c) => s + c.unread, 0);

  // ── nav back ────────────────────────────────────────────────────────────────
  const handleBack = () => {
    const role = user?.role;
    if (role === 'recruiter') navigate('/recruiter/dashboard');
    else if (role === 'student') navigate('/student/dashboard');
    else navigate('/');
  };

  // ── active convo display name ───────────────────────────────────────────────
  const activeName = activeOther?.other?.name || '';
  const activeSub  = activeOther?.other?.company_name || (activeOther?.otherType === 'Recruiter' ? 'Recruiter' : 'Student');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg)', fontFamily: 'var(--font)' }}>

      {/* ── Top nav ── */}
      <nav style={{ background: '#fff', borderBottom: '1px solid var(--border)', height: 56, padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={handleBack} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Dashboard
          </button>
          <div style={{ width: 1, height: 20, background: 'var(--border)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>Messages</span>
            {totalUnread > 0 && (
              <span style={{ background: 'var(--primary)', color: '#fff', borderRadius: 20, fontSize: 11, fontWeight: 700, padding: '2px 8px' }}>{totalUnread}</span>
            )}
          </div>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: 14, color: 'var(--text-muted)', letterSpacing: '-0.3px' }}><img src={logoMark} alt="" className="brand-mark" />HYRSKILL</span>
      </nav>

      {/* ── Two-panel body ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ═══ LEFT PANEL — conversation list ═══ */}
        <div className={`msg-left-panel${mobileChatOpen ? ' mobile-hidden' : ''}`} style={{ width: 320, flexShrink: 0, borderRight: '1px solid var(--border)', background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Search bar */}
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <div style={{ position: 'relative' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text" placeholder="Search conversations…" value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 9, border: '1.5px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg)', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {convLoading && (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
            )}
            {!convLoading && filtered.length === 0 && (
              <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ opacity: 0.3, marginBottom: 10 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                <p style={{ fontWeight: 600, fontSize: 13 }}>{search ? 'No matches' : 'No conversations yet'}</p>
              </div>
            )}
            {!convLoading && filtered.map(c => (
              <div key={c.key} style={{ position: 'relative' }}>
                <ConvoItem
                  c={c}
                  myId={myId}
                  active={activeKey === c.key}
                  onClick={() => openConvo(c)}
                />
                {/* Delete button — appears on hover via CSS class would need stylesheet; using opacity trick */}
                <button
                  onClick={(e) => handleDelete(c.otherId, e)}
                  title="Delete conversation"
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', opacity: 0.5, padding: 4, borderRadius: 6, fontSize: 16, lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ RIGHT PANEL — thread ═══ */}
        <div className={`msg-right-panel${!mobileChatOpen ? ' mobile-hidden' : ''}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* No active convo */}
          {!activeOther && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ opacity: 0.2, marginBottom: 16 }}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <p style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>Select a conversation</p>
              <p style={{ fontSize: 13 }}>Pick one from the left or start a new chat from a job applicant.</p>
            </div>
          )}

          {/* Active convo */}
          {activeOther && (
            <>
              {/* Thread header */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', background: '#fff', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                {/* Back button — mobile only */}
                <button
                  onClick={() => setMobileChatOpen(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'var(--text-secondary)', padding: '4px 0', marginRight: 4, flexShrink: 0 }}
                  className="msg-back-btn"
                  title="Back to conversations"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: 'var(--primary)' }}>
                  {initials(activeName)}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{activeName || 'Unknown'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{activeSub}</div>
                </div>
              </div>

              {/* Messages area */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', background: '#F8FAFC' }}>
                {threadLoading && (
                  <div style={{ textAlign: 'center', paddingTop: 60, color: 'var(--text-muted)', fontSize: 13 }}>Loading messages…</div>
                )}
                {!threadLoading && thread.length === 0 && (
                  <div style={{ textAlign: 'center', paddingTop: 60, color: 'var(--text-muted)', fontSize: 13 }}>
                    No messages yet. Send the first one below.
                  </div>
                )}
                {!threadLoading && thread.map(msg => {
                  const isMine = msg.sender_id?.toString() === myId || msg.sender_id === myId;
                  return <Bubble key={msg._id} msg={msg} isMine={isMine} />;
                })}
                <div ref={bottomRef} />
              </div>

              {/* Compose box */}
              <div style={{ borderTop: '1px solid var(--border)', background: '#fff', padding: '12px 16px', flexShrink: 0 }}>
                {/* File preview */}
                {file && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#EEF2FF', borderRadius: 8, marginBottom: 8, fontSize: 13 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                    <span style={{ flex: 1, color: 'var(--primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                    <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontWeight: 700, fontSize: 16 }}>×</button>
                  </div>
                )}
                <form onSubmit={handleSend} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <textarea
                    value={body}
                    onChange={e => setBody(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
                    placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
                    rows={1}
                    style={{
                      flex: 1, padding: '10px 14px', borderRadius: 12, border: '1.5px solid var(--border)',
                      fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit',
                      maxHeight: 120, overflowY: 'auto',
                    }}
                  />
                  {/* Attach button */}
                  <button type="button" onClick={() => fileRef.current?.click()} title="Attach file"
                    style={{ padding: '10px 12px', borderRadius: 10, border: '1.5px solid var(--border)', background: '#fff', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  </button>
                  <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0] || null)} />
                  {/* Send button */}
                  <button type="submit" disabled={sending || (!body.trim() && !file)}
                    style={{ padding: '10px 18px', borderRadius: 10, background: 'var(--primary)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 14, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {sending
                      ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                      : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    }
                    Send
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
